/**
 * Cart bundles
 *
 * Line items that share a hidden `_bundle_id` property are rendered by
 * `snippets/cart-bundle-group.liquid` as one collapsible "bundle" row plus the
 * member rows. This script wires up:
 *   - expanding / collapsing the member rows
 *   - changing the bundle quantity (all member lines together)
 *   - removing the whole bundle (and dropping its discount code when no other
 *     bundle in the cart still needs it)
 *
 * Everything is event-delegated on `document`, because the cart drawer and the
 * cart page replace their inner HTML after every cart change.
 */

(function () {
  if (window.CartBundles) return;

  const routes = () => {
    const r = window.routes || {};
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return {
      cart_update_url: r.cart_update_url || root + 'cart/update',
      cart_url: r.cart_url || root + 'cart',
    };
  };

  function jsonFetchConfig() {
    if (typeof fetchConfig === 'function') return fetchConfig();
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };
  }

  function cssEscape(value) {
    return window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&');
  }

  /* ------------------------------------------------------------------ */
  /* Expand / collapse                                                    */
  /* ------------------------------------------------------------------ */

  function toggleBundle(button) {
    const bundleId = button.dataset.bundleToggle;
    const header = button.closest('[data-bundle-header]');
    const scope = header ? header.closest('table') || document : document;
    const rows = scope.querySelectorAll(`[data-bundle-group="${cssEscape(bundleId)}"]`);
    const expanded = button.getAttribute('aria-expanded') === 'true';

    rows.forEach((row) => {
      row.hidden = expanded;
    });
    button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (header) header.classList.toggle('is-collapsed', expanded);

    const text = button.querySelector('.cart-bundle__toggle-text');
    if (text) text.textContent = expanded ? button.dataset.showText : button.dataset.hideText;
  }

  /* ------------------------------------------------------------------ */
  /* Quantity / remove                                                    */
  /* ------------------------------------------------------------------ */

  function getCartItemsElement(node) {
    return node.closest('cart-drawer-items') || node.closest('cart-items');
  }

  function getSectionsToRender(cartItems) {
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender();
    }
    // Fallback for the drawer
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  function renderSections(parsedState, sections) {
    sections.forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container || !parsedState.sections || !parsedState.sections[section.section]) return;
      const target = container.querySelector(section.selector) || container;
      const html = new DOMParser().parseFromString(parsedState.sections[section.section], 'text/html');
      const source = html.querySelector(section.selector);
      if (source) target.innerHTML = source.innerHTML;
    });
  }

  function setLoading(header, cartItems, loading) {
    const container =
      document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (container) container.classList.toggle('cart__items--disabled', loading);
    if (header) {
      header.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.toggle('hidden', !loading));
      header.classList.toggle('is-loading', loading);
    }
    const status =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');
    if (status) status.setAttribute('aria-hidden', loading ? 'false' : 'true');
  }

  function showError(header, message) {
    const target = header && header.querySelector('[data-bundle-error] .cart-item__error-text');
    if (target) {
      target.textContent = message;
      return;
    }
    const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (errors) errors.textContent = message;
  }

  /**
   * After a bundle is removed, drop its discount code unless another bundle in
   * the cart still relies on the same code. Codes the customer typed themselves
   * (not attached to any bundle) are never touched.
   */
  function reconcileDiscountCodes(cart, removedCode) {
    if (!removedCode) return null;
    const stillNeeded = (cart.items || []).some(
      (item) => item.properties && item.properties._bundle_code === removedCode
    );
    if (stillNeeded) return null;

    const current = (cart.discount_codes || []).map((entry) => entry.code).filter(Boolean);
    if (!current.includes(removedCode)) return null;

    return current.filter((code) => code !== removedCode).join(',');
  }

  async function updateBundle(control, quantity) {
    if (control.dataset.busy === 'true') return;
    const keys = (control.dataset.bundleKeys || '').split(',').filter(Boolean);
    if (!keys.length) return;

    const cartItems = getCartItemsElement(control);
    const header = control.closest('[data-bundle-header]');
    const sections = getSectionsToRender(cartItems);
    const code = (control.dataset.bundleCode || '').trim();
    const previousQuantity = parseInt(control.dataset.quantity, 10) || 1;

    control.dataset.busy = 'true';
    setLoading(header, cartItems, true);
    if (document.activeElement && typeof document.activeElement.blur === 'function') document.activeElement.blur();

    const updates = {};
    keys.forEach((key) => {
      updates[key] = quantity;
    });

    try {
      let state = await fetch(routes().cart_update_url, {
        ...jsonFetchConfig(),
        body: JSON.stringify({
          updates,
          sections: sections.map((section) => section.section),
          sections_url: window.location.pathname,
        }),
      }).then((response) => response.json());

      if (state.status || state.errors) {
        showError(header, state.description || state.errors || (window.cartStrings && window.cartStrings.error));
        const input = control.querySelector('[data-bundle-qty-input]');
        if (input) input.value = previousQuantity;
        return;
      }

      if (quantity === 0) {
        const remaining = reconcileDiscountCodes(state, code);
        if (remaining !== null) {
          const discountState = await fetch(routes().cart_update_url, {
            ...jsonFetchConfig(),
            body: JSON.stringify({
              discount: remaining,
              sections: sections.map((section) => section.section),
              sections_url: window.location.pathname,
            }),
          }).then((response) => response.json());
          if (!discountState.status && discountState.sections) state = discountState;
        }
      }

      const isEmpty = state.item_count === 0;
      if (cartItems) cartItems.classList.toggle('is-empty', isEmpty);
      const drawer = document.querySelector('cart-drawer');
      if (drawer) drawer.classList.toggle('is-empty', isEmpty);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty);

      renderSections(state, sections);

      if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: state });
      }

      if (drawer && drawer.classList.contains('active')) {
        const focusTarget = isEmpty
          ? drawer.querySelector('.drawer__inner-empty a, .drawer__close')
          : drawer.querySelector('.cart-bundle__title, .cart-item__name');
        if (focusTarget && typeof trapFocus === 'function') trapFocus(drawer, focusTarget);
      }
    } catch (error) {
      console.error('[cart-bundles] update failed', error);
      showError(header, (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart.');
    } finally {
      control.dataset.busy = 'false';
      setLoading(header, cartItems, false);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Event delegation                                                     */
  /* ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-bundle-toggle]');
    if (toggle) {
      event.preventDefault();
      toggleBundle(toggle);
      return;
    }

    const remove = event.target.closest('[data-bundle-remove]');
    if (remove) {
      event.preventDefault();
      const control = remove.closest('cart-bundle-quantity');
      if (control) updateBundle(control, 0);
      return;
    }

    const minus = event.target.closest('[data-bundle-qty-minus]');
    const plus = event.target.closest('[data-bundle-qty-plus]');
    if (minus || plus) {
      event.preventDefault();
      const control = (minus || plus).closest('cart-bundle-quantity');
      if (!control) return;
      const input = control.querySelector('[data-bundle-qty-input]');
      const current = parseInt((input && input.value) || control.dataset.quantity, 10) || 0;
      const next = Math.max(0, current + (plus ? 1 : -1));
      if (input) input.value = next;
      updateBundle(control, next);
    }
  });

  // Capture-phase listener so the theme's own cart quantity handler
  // (which expects a line index) never sees changes from bundle inputs.
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target.closest && event.target.closest('[data-bundle-qty-input]');
      if (!input) return;
      event.stopPropagation();
      const control = input.closest('cart-bundle-quantity');
      if (!control) return;
      const value = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = value;
      updateBundle(control, value);
    },
    true
  );

  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Enter') return;
      const input = event.target.closest && event.target.closest('[data-bundle-qty-input]');
      if (!input) return;
      event.preventDefault();
      input.blur();
    },
    true
  );

  window.CartBundles = { updateBundle, toggleBundle };
})();
